import { Box } from "@mui/material";
import type { ReactNode } from "react";

/**
 * `<DataPageToolbar>` — canonical two-row toolbar for list pages.
 *
 * Spec: `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 2".
 *
 * Row 1 (filters): `display: flex; flex-wrap: wrap; gap: 16px;` — modern
 * CSS `gap`, NOT the legacy MUI `Stack` margin shim, so wrapped rows get
 * vertical breathing room.
 *
 * Row 2 (settings): same flex+wrap but with `gap: 8px`, a top divider, and
 * `width: 100%` so the divider spans the full table column (load-bearing —
 * Christian's 2026-05-13 feedback "the grey separation line below filter
 * settings is shorter than our actual table width").
 *
 * Both rows tag with the caller-supplied `data-testid` so the acceptance
 * specs can query them by stable selector.
 */
export interface DataPageToolbarProps {
  ariaLabel: string;
  filtersTestId: string;
  settingsTestId: string;
  filters: ReactNode;
  /**
   * Make the FILTER row comfortable (44px) even on a dense surface.
   *
   * D77 keeps density a property of a region, and this is a region whose
   * density differs from its surface on purpose: on the customer app's list
   * pages the filter row is the main tool — the operator is there to narrow a
   * fleet — while the table below it stays dense. Off by default, so the
   * console's own toolbars are untouched.
   */
  comfortableFilters?: boolean;
  settings: ReactNode;
}

export function DataPageToolbar({
  ariaLabel,
  filtersTestId,
  settingsTestId,
  filters,
  settings,
  comfortableFilters = false,
}: DataPageToolbarProps): React.ReactElement {
  return (
    <Box
      role="toolbar"
      aria-label={ariaLabel}
      sx={{
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        // Explicit full width — without it the row 2 borderTop divider
        // can collapse below the parent's cross-axis size under certain
        // layout shifts (Christian, 2026-05-13).
        width: "100%",
      }}
    >
      {/* Row 1 — filters. Uniform `gap: 2` (16 px) so the wrap rhythm is
          even at every viewport. */}
      <Box
        data-testid={filtersTestId}
        {...(comfortableFilters ? { "data-density": "comfortable" } : {})}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        {filters}
      </Box>

      {/* Row 2 — table-settings. 1 px top divider + 8 px gap. The
          `width: 100%` is load-bearing for the divider span. */}
      <Box
        data-testid={settingsTestId}
        sx={(t) => ({
          mt: 1,
          pt: 1,
          borderTop: `1px solid ${t.palette.divider}`,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          width: "100%",
        })}
      >
        {settings}
      </Box>
    </Box>
  );
}
